import type { GridSize } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';

import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import type { ButtonContent } from '@/components/OldButton/OldButton';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import {
  type Contentful,
  ContentStyle,
  type FeaturedComponent,
  type FrontifyImageContent,
} from '@/contentful';

import HeaderAndCTA from './HeaderAndCTA/HeaderAndCTA';
import MultipleStacks from './MultipleStacks/MultipleStacks';
import useSharedStyles from './SharedStackedStyles.styles';
import SingleStack from './SingleStack/SingleStack';

type ImageStackedFields = {
  name: string;
  backgroundImages?: FrontifyImageContent[];
  subhead?: string;
  body?: string;
  cta?: ButtonContent;
};

export interface ImageTextStackedXup extends Contentful {
  fields: ImageStackedFields;
}

export interface HighlightImageTextStackedContent extends Contentful {
  fields: {
    id: string;
    variant: string;
    style?: ContentStyle;
    eyebrow?: string;
    headline?: string;
    subheading?: string;
    headerCta?: ButtonContent;
    imageTextStackedXup?: ImageTextStackedXup[];
  };
}

export type HighlightImageTextStackedProps = FeaturedComponent<{
  content: HighlightImageTextStackedContent;
}>;

export enum ImageVariant {
  '1UP' = '1UP',
  '2UP' = '2UP',
  '3UP' = '3UP',
  '4UP' = '4UP',
}

export default function HighlightImageTextStacked({
  isFeatured,
  content: {
    fields: {
      id,
      variant,
      style = ContentStyle.OnDark,
      eyebrow,
      headline,
      subheading,
      headerCta,
      imageTextStackedXup,
    },
  },
}: HighlightImageTextStackedProps) {
  const isOnDark = style === ContentStyle.OnDark;
  const shouldShowHeaderCTA = headerCta && variant !== ImageVariant['1UP'];
  const { classes: sharedClassees } = useSharedStyles({ isOnDark });

  let gridSizes: Record<string, GridSize> = { xs: 6, sm: 6, md: 6, lg: 6 };

  switch (variant) {
    case ImageVariant['2UP']:
      break;
    case ImageVariant['3UP']:
      gridSizes = { xs: 6, sm: 6, md: 4, lg: 4 };
      break;
    case ImageVariant['4UP']:
      gridSizes = { xs: 6, sm: 6, md: 3, lg: 3 };
      break;
    default:
      break;
  }

  return (
    <SpacingContainer
      isFeatured={isFeatured}
      id={`highlight-stack ${id}`}
      className={sharedClassees.container}
    >
      <HeaderAndCTA
        isOnDark={isOnDark}
        eyebrow={eyebrow}
        subheading={subheading}
        headline={headline}
        headerCta={shouldShowHeaderCTA ? headerCta : undefined}
      />
      <GridContainer2 fixed>
        {/** biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome */}
        {imageTextStackedXup &&
          imageTextStackedXup.map(
            (stack: ImageTextStackedXup, index: number) => {
              const key = `${stack?.fields?.name}${index}`;
              if (variant === '1UP') {
                return (
                  <SingleStack key={key} content={stack} isOnDark={isOnDark} />
                );
              } else {
                return (
                  <Grid2 key={key} {...gridSizes}>
                    <MultipleStacks
                      key={key}
                      content={stack}
                      variant={variant}
                      isOnDark={isOnDark}
                      index={index}
                      shouldShowHeaderCTA={shouldShowHeaderCTA}
                    />
                  </Grid2>
                );
              }
            }
          )}
      </GridContainer2>
    </SpacingContainer>
  );
}
